import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Calendar, DollarSign } from "lucide-react";
import { OnboardingData } from "../index";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";

interface FinancialSetupProps {
  onNext: () => void;
  onPrevious: () => void;
  onboardingData: OnboardingData;
  updateOnboardingData: (data: Partial<OnboardingData>) => void;
}

const financialSetupSchema = z.object({
  fiscalYearEnd: z.date().nullable().optional(),
  accountingMethod: z.string().min(1, "Please select an accounting method"),
  taxFilingFrequency: z.string().min(1, "Please select a tax filing frequency"),
  baseCurrency: z.string().min(1, "Please select a base currency"),
});

type FinancialSetupFormValues = z.infer<typeof financialSetupSchema>;

export default function FinancialSetup({
  onNext,
  onPrevious,
  onboardingData,
  updateOnboardingData,
}: FinancialSetupProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  
  const form = useForm<FinancialSetupFormValues>({
    resolver: zodResolver(financialSetupSchema),
    defaultValues: {
      fiscalYearEnd: onboardingData.fiscalYearEnd || null,
      accountingMethod: onboardingData.accountingMethod || "",
      taxFilingFrequency: onboardingData.taxFilingFrequency || "",
      baseCurrency: onboardingData.baseCurrency || "USD",
    },
  });
  
  const onSubmit = async (values: FinancialSetupFormValues) => {
    try {
      setIsLoading(true);
      
      // Store data in onboarding context
      updateOnboardingData({
        fiscalYearEnd: values.fiscalYearEnd,
        accountingMethod: values.accountingMethod,
        taxFilingFrequency: values.taxFilingFrequency,
        baseCurrency: values.baseCurrency,
      });
      
      // Fake API delay for demo purposes
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Success notification
      toast({
        title: "Financial setup saved",
        description: "Your financial preferences have been saved successfully.",
      });
      
      // Continue to next step
      onNext();
    } catch (error) {
      toast({
        title: "Error",
        description: "There was a problem saving your financial setup. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="max-w-2xl mx-auto">
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Financial Setup</h2>
          <p className="mt-2 text-neutral-600">
            Set up your financial preferences for accurate bookkeeping
          </p>
        </div>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="fiscalYearEnd"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Fiscal Year End Date</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  "pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}
                              >
                                <Calendar className="mr-2 h-4 w-4 opacity-70" />
                                {field.value ? (
                                  format(field.value, "MMMM d, yyyy")
                                ) : (
                                  <span>Select fiscal year end date</span>
                                )}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={field.value || undefined}
                              onSelect={field.onChange}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormDescription>
                          If your fiscal year differs from the calendar year, select the end date. Otherwise, December 31 is assumed.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="accountingMethod"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Accounting Method</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select accounting method" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="cash">Cash Basis</SelectItem>
                            <SelectItem value="accrual">Accrual Basis</SelectItem>
                            <SelectItem value="hybrid">Hybrid Method</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Cash basis records income when received and expenses when paid. Accrual basis records them when earned or incurred.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="taxFilingFrequency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tax Filing Frequency</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select tax filing frequency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="quarterly">Quarterly</SelectItem>
                            <SelectItem value="annually">Annually</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          How often your business is required to file taxes
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="baseCurrency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Base Currency</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <DollarSign className="h-4 w-4 mr-2 opacity-70" />
                              <SelectValue placeholder="Select base currency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="USD">USD - US Dollar</SelectItem>
                            <SelectItem value="EUR">EUR - Euro</SelectItem>
                            <SelectItem value="GBP">GBP - British Pound</SelectItem>
                            <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                            <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                            <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                            <SelectItem value="CNY">CNY - Chinese Yuan</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          The primary currency you'll use for your bookkeeping
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-medium mb-4">Chart of Accounts</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  ZentroBooks will automatically generate a Chart of Accounts based on your business type and industry. 
                  You'll be able to customize it later.
                </p>
                
                <div className="bg-primary/5 p-4 rounded-md border border-primary/10">
                  <h4 className="font-medium text-primary mb-2">What is a Chart of Accounts?</h4>
                  <p className="text-sm text-neutral-700">
                    A Chart of Accounts is an organized list of all financial accounts used in your business's general ledger. 
                    It includes asset accounts, liability accounts, equity accounts, revenue accounts, and expense accounts.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <div className="flex justify-between pt-4">
              <Button type="button" variant="outline" onClick={onPrevious}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  "Saving..."
                ) : (
                  <>
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}